import auth from '@client/components/auth';
import { PATHS } from '@client/routes';
import { ButtonLink } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { faReply } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Rating } from '@mui/material';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { isEmpty } from 'lodash';

type FeedbackItem = {
  id: number;
  name: string;
  thumbnail: string;
  rating: number | null;
  workoutName: string;
  text: string;
  createdAt: number;
  userId?: number;
};

type FeedbackProps = {
  show: boolean;
  data: Array<FeedbackItem>;
  dmEnabled: boolean;
  influencerId: number;
};

export const Feedback = ({
  show,
  data,
  dmEnabled,
  influencerId,
}: FeedbackProps) => {
  if (!show) return null;

  if (isEmpty(data)) {
    return (
      <div className="flex flex-col gap-6 bg-white rounded-[20px] py-4 px-6">
        <Typography variant="headline" size="medium">
          Workout Feedback
        </Typography>
        <Typography
          variant="body"
          size="medium"
          className={clsx('font-normal', sprinkles({ color: 'N-60' }))}
        >
          Ratings and comments on your workouts
        </Typography>
        <div className="flex flex-col gap-4 justify-center items-center">
          <Typography
            variant="body"
            size="large"
            className={clsx('font-bold', sprinkles({ color: 'N-60' }))}
          >
            No feedback left yet!
          </Typography>
          <Typography
            variant="body"
            size="large"
            className={clsx('font-normal', sprinkles({ color: 'N-60' }))}
          >
            Comments left on your workouts and suggestions sent through your
            channel appear here.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 bg-white rounded-[20px] py-4 px-6">
      <Typography variant="headline" size="medium">
        Workout Feedback
      </Typography>
      <Typography
        variant="body"
        size="medium"
        className={clsx('font-normal', sprinkles({ color: 'N-60' }))}
      >
        Ratings and comments on your workouts
      </Typography>
      <div className="relative">
        <div className="flex flex-col gap-4 h-[388px] overflow-y-scroll">
          {data.map(
            ({
              id,
              name,
              thumbnail,
              rating,
              workoutName,
              text,
              createdAt,
              userId,
            }) => (
              <div
                key={id}
                className={clsx(
                  sprinkles({ bg: 'N-10' }),
                  'rounded-[10px] flex flex-col md:flex-row md:items-center justify-between gap-3 p-3',
                )}
              >
                {' '}
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2.5">
                    <Avatar src={thumbnail} />
                    <div className="flex flex-col gap-1">
                      <Typography variant="body" size="medium">
                        {name}
                      </Typography>
                      {rating !== null &&
                        Number.isFinite(rating) &&
                        rating > 0 && (
                          <Rating
                            value={rating}
                            max={5}
                            precision={0.5}
                            readOnly
                            size="small"
                          />
                        )}
                    </div>
                  </div>
                  {text && (
                    <Typography
                      variant="body"
                      size="medium"
                      className="text-normal"
                    >
                      {text}
                    </Typography>
                  )}
                  <div className="flex items-center gap-1 text-[#8A8E99] text-[10px] text-normal">
                    <Typography className="text-[10px] text-normal">
                      {getTimeLabel(createdAt)} ago
                    </Typography>
                    <Typography className="text-[10px] text-normal">
                      |
                    </Typography>
                    <Typography className="text-[10px] text-normal">
                      {workoutName}
                    </Typography>
                  </div>
                </div>
                <div>
                  {dmEnabled && userId && (
                    <ButtonLink
                      to={
                        auth.isInfluencer()
                          ? PATHS.INBOX(userId)
                          : PATHS.INBOX_MESSAGE(influencerId, userId)
                      }
                      variant="outlined"
                      color="neutral"
                      className="flex gap-1 items-center"
                    >
                      <FontAwesomeIcon icon={faReply} />
                      reply via dm
                    </ButtonLink>
                  )}
                </div>
              </div>
            ),
          )}
        </div>
        <div className="w-full h-[68px] absolute bottom-0 bg-gradient-to-t from-white to-[#ff0000 50%]" />
      </div>
    </div>
  );
};

const getTimeLabel = (createdAt: number) =>
  formatDistanceToNow(new Date(createdAt));
